export type FilterParamValue = string | boolean | string[] | undefined;
export type FilterParams = Record<string, FilterParamValue>;

type SetFilterModel = {
  filterType: 'set';
  values: string[];
};

type BooleanFilterModel = {
  filterType: 'boolean';
  filter: boolean;
};

type TextFilterModel = {
  filterType: 'text';
  type: 'contains';
  filter: string;
};

type DateRangeFilterModel = {
  filterType: 'dateRange';
  startDate?: string;
  endDate?: string;
};

type FilterModelValue =
  | SetFilterModel
  | BooleanFilterModel
  | TextFilterModel
  | DateRangeFilterModel;

export type FilterModel = Record<string, FilterModelValue>;

const IMPORTS_URL_TO_FILTER_MODEL_MAPPING = {
  dateFrom: ['importDate', 'importDateFilterPanel'],
  dateTo: ['importDate', 'importDateFilterPanel'],
};

const TEMPLATES_URL_TO_FILTER_MODEL_MAPPING = {
  dateFrom: ['createdDate', 'createdDateFilterPanel'],
  dateTo: ['createdDate', 'createdDateFilterPanel'],
};

const getTargetFields = (
  urlParamKey: string,
  customURLToFilterModelMapping: Record<string, string | string[]>,
) => {
  const filterModelFieldName =
    customURLToFilterModelMapping[urlParamKey] ?? urlParamKey;

  return Array.isArray(filterModelFieldName)
    ? filterModelFieldName
    : [filterModelFieldName];
};

const createDateRangeFilter = (
  filterParams: FilterParams,
): DateRangeFilterModel | null => {
  const dateFrom = filterParams.dateFrom;
  const dateTo = filterParams.dateTo;

  if (!dateFrom && !dateTo) {
    return null;
  }

  return {
    filterType: 'dateRange',
    startDate: typeof dateFrom === 'string' ? dateFrom : undefined,
    endDate: typeof dateTo === 'string' ? dateTo : undefined,
  };
};

const createFilterModelValue = (
  value: FilterParamValue,
): FilterModelValue | null => {
  if (Array.isArray(value)) {
    return {
      filterType: 'set',
      values: value,
    };
  }

  if (typeof value === 'boolean') {
    return {
      filterType: 'boolean',
      filter: value,
    };
  }

  if (typeof value === 'string') {
    return {
      filterType: 'text',
      type: 'contains',
      filter: value,
    };
  }

  return null;
};

export const createImportsFilterModelFromUrlParams = (
  filterParams: FilterParams,
) => {
  return createFilterModelFromUrlParams(
    filterParams,
    IMPORTS_URL_TO_FILTER_MODEL_MAPPING,
  );
};

export const createTemplatesFilterModelFromUrlParams = (
  filterParams: FilterParams,
) => {
  return createFilterModelFromUrlParams(
    filterParams,
    TEMPLATES_URL_TO_FILTER_MODEL_MAPPING,
  );
};

const createFilterModelFromUrlParams = (
  filterParams: FilterParams,
  customURLToFilterModelMapping: Record<string, string | string[]>,
) => {
  if (!Object.keys(filterParams).length) {
    return {};
  }

  const filterModel: FilterModel = {};

  Object.entries(filterParams).forEach(([urlParamKey, value]) => {
    const targetFields = getTargetFields(
      urlParamKey,
      customURLToFilterModelMapping,
    );
    const filter =
      urlParamKey === 'dateFrom'
        ? createDateRangeFilter(filterParams)
        : createFilterModelValue(value);

    if (urlParamKey === 'dateTo' || !filter) {
      return;
    }

    targetFields.forEach((filterModelFieldName) => {
      filterModel[filterModelFieldName] = filter;
    });
  });

  return filterModel;
};

export const transformFilterModelToAPIParams = (
  filterModel: null | FilterModel,
) => {
  const transformedFilters: Record<string, string | boolean | string[]> = {};

  if (!filterModel) {
    return transformedFilters;
  }

  Object.keys(filterModel).forEach((field) => {
    const filter = filterModel[field];

    if (filter.filterType === 'set') {
      if (filter.values.length > 0) {
        transformedFilters[field] = filter.values;
      }
      return;
    }

    if (filter.filterType === 'dateRange') {
      if (filter.startDate) {
        transformedFilters.dateFrom = filter.startDate;
      }
      if (filter.endDate) {
        transformedFilters.dateTo = filter.endDate;
      }
      return;
    }

    if (filter.filter) {
      transformedFilters[field] = filter.filter;
    }
  });

  return transformedFilters;
};

export const convertFilterModelToURLParams = (filterModel: FilterModel) => {
  const filtersParams: Record<string, string | string[]> = {};

  Object.entries(filterModel).forEach(([fieldName, filter]) => {
    if (filter.filterType === 'dateRange') {
      if (filter.startDate) {
        filtersParams.dateFrom = filter.startDate;
      }
      if (filter.endDate) {
        filtersParams.dateTo = filter.endDate;
      }
      return;
    }

    if (filter.filterType === 'set') {
      filtersParams[fieldName] = filter.values;
      return;
    }

    if (['text', 'boolean'].includes(filter.filterType)) {
      filtersParams[fieldName] = String(filter.filter);
    }
  });

  return filtersParams;
};

export const convertSortModelToURLParams = (
  sortModel: Array<{ colId: string; sort: 'asc' | 'desc' }>,
) => {
  if (!sortModel || sortModel.length === 0) {
    return { sortBy: undefined, sortDirection: undefined };
  }

  const { colId, sort } = sortModel[0];

  return {
    sortBy: colId,
    sortDirection: sort === 'desc' ? 'desc' : 'asc',
  };
};

export const createSortModelFromURLParams = (
  sortBy?: string,
  sortDirection?: 'asc' | 'desc',
) => {
  if (!sortBy || !sortDirection) {
    return [];
  }

  return [
    {
      colId: sortBy,
      sort: sortDirection,
    },
  ];
};
